import { useState, useEffect, useCallback } from 'react';
import { Eye, Building2 } from 'lucide-react';
import toast from '../lib/toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * F18 amendment (2026-09-23/24, Jaime): per-condo directory opt-out UI.
 * PUT /api/condos/:condoId/my-directory-visibility { visible: boolean } —
 * updates ALL active memberships of the caller in that condo. FALSE =
 * BIDIRECTIONAL invisibility: you disappear from the condo roster AND you
 * cannot see the roster yourself. Rendered inside /edicion-perfil (privacy).
 *
 * NOTE: the backend exposes no GET for the per-condo flag, so the initial
 * toggle state defaults to ON (backend default is TRUE via COALESCE) and is
 * kept in sync from the toggle's own responses within this session.
 */
export default function DirectoryPrivacyCard() {
  const { memberships } = useAuth();
  const condos = (memberships || []).filter((m: any) => m?.condoId);
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Default every known condo to visible=true (backend default).
    setVisibility(prev => {
      const next = { ...prev };
      condos.forEach((m: any) => { if (next[m.condoId] === undefined) next[m.condoId] = true; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberships?.length]);

  const toggle = useCallback(async (condoId: string, current: boolean) => {
    const next = !current;
    setPending(p => ({ ...p, [condoId]: true }));
    setVisibility(v => ({ ...v, [condoId]: next })); // optimistic
    try {
      const res = await api.put(`/condos/${condoId}/my-directory-visibility`, { visible: next });
      const serverVisible = res.data?.data?.directoryVisible;
      if (typeof serverVisible === 'boolean') setVisibility(v => ({ ...v, [condoId]: serverVisible }));
      toast.success(next
        ? 'Ahora eres visible en el directorio de este condominio'
        : 'Ya no apareces en el directorio de este condominio');
    } catch (err: any) {
      setVisibility(v => ({ ...v, [condoId]: current })); // revert
      const status = err?.response?.status;
      if (status === 403) toast.error('No tienes acceso para cambiar la visibilidad en este condominio');
      else toast.error(err?.response?.data?.error || 'No se pudo actualizar la visibilidad');
    } finally {
      setPending(p => ({ ...p, [condoId]: false }));
    }
  }, []);

  if (!condos.length) return null;

  return (
    <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6 mb-6">
      <h3 className="font-bold text-[var(--v-text-primary)] mb-1 flex items-center gap-2">
        <Eye className="w-5 h-5 text-[var(--v-teal-400)]" /> Privacidad del directorio
      </h3>
      <p className="text-sm text-[var(--v-text-secondary)] mb-4">
        Controla si apareces en el directorio de vecinos de cada condominio. Si te ocultas en un
        condominio, dejarás de ver su directorio también: la invisibilidad es bidireccional y se
        aplica por condominio. Los administradores siguen pudiendo gestionar las unidades con normalidad.
      </p>
      <div className="space-y-3">
        {condos.map((m: any) => {
          const visible = visibility[m.condoId] ?? true;
          return (
            <div key={m.condoId} className="flex items-center justify-between p-4 bg-[var(--v-surface-2)] rounded-xl">
              <div className="flex items-center gap-3 min-w-0">
                <Building2 className="w-5 h-5 text-[var(--v-text-tertiary)] flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-[var(--v-text-primary)] font-medium block truncate">
                    Visible en el directorio{m.condoName ? ` — ${m.condoName}` : ''}
                  </span>
                  <span className="text-xs text-[var(--v-text-secondary)]">
                    {visible
                      ? 'Otros vecinos pueden verte y tú puedes ver el directorio.'
                      : 'No apareces en el directorio y no puedes verlo (bidireccional).'}
                  </span>
                </div>
              </div>
              <button
                role="switch"
                aria-checked={visible}
                disabled={!!pending[m.condoId]}
                onClick={() => toggle(m.condoId, visible)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${visible ? 'bg-[var(--v-teal-400)]' : 'bg-[var(--v-surface-3)]'}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${visible ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                <span className="sr-only">Visible en el directorio</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
